import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  TextField,
  Button,
  Stack,
  RadioGroup,
  Radio,
  CircularProgress,
} from '@mui/material';
import Footer from '../components/Footer';
import {BookingView} from '../types';
import {BookingDataUpdater} from '../types';
import {cancelFormViewStyles} from '../styles';
import CancelImage from '../../../assets/svgs/cancel-icon.svg';
import CancelImageBackground from '../../../assets/svgs/cancel-icon-background.svg';
import {useCancellationReasons} from '../hooks';
import FetchError from '$components/FetchError';
import {CancellationReasonWithId} from '../types';

const TEXT = {
  TITLE: 'Why do you need to cancel?',
  CANCEL_BUTTON: 'Cancel',
  REVIEW_BUTTON: 'Review changes',
  OTHER_PLACEHOLDER: 'Specify other motivation',
  CANCEL_BACKGROUND_ALT: 'Cancel background',
  CANCEL_ICON_ALT: 'Cancel icon',
  OTHER_REASON: 'Other (please specify)',
  OTHER_REASON_ERROR: 'Other reason must not exceed 200 characters',
};

interface CancelFormViewProps {
  setActiveView: (view: BookingView) => void;
  setBookingChoices: BookingDataUpdater;
}

const CancelFormView = ({
  setActiveView,
  setBookingChoices,
}: CancelFormViewProps) => {
  const {cancellationReasons, isLoading, isError} = useCancellationReasons();

  useEffect(() => {
    if (cancellationReasons && cancellationReasons.length > 0) {
      console.log('Available cancellation reasons:', cancellationReasons);
    }
  }, [cancellationReasons]);
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const [otherText, setOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otherTextError, setOtherTextError] = useState<string>('');

  const isOtherReason = (reason: {name: string} | undefined) =>
    reason?.name === TEXT.OTHER_REASON;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedId = event.target.value;
    setSelectedReasonId(newSelectedId);

    const selectedReason = cancellationReasons.find(
      reason => reason.id.toString() === newSelectedId
    );

    if (selectedReason && !isOtherReason(selectedReason)) {
      setOtherText('');
      setOtherTextError('');
    }
  };

  const handleOtherTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOtherText(value);

    if (value.length > 200) {
      setOtherTextError(TEXT.OTHER_REASON_ERROR);
    } else {
      setOtherTextError('');
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    const selectedReason = cancellationReasons.find(
      reason => reason.id.toString() === selectedReasonId
    );

    if (!selectedReason) {
      setIsSubmitting(false);
      return;
    }

    const cancelReasons: CancellationReasonWithId = {
      reasonId: selectedReason.id,
      reasonName: selectedReason.name,
      ...(isOtherReason(selectedReason) &&
        otherText && {otherReason: otherText}),
    };

    setBookingChoices(prev => ({
      ...prev,
      cancelReasons,
    }));

    setActiveView(BookingView.ReviewCancelSummary);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setActiveView(BookingView.CancelConfirmationView);
  };

  const isOtherSelected = Boolean(
    selectedReasonId &&
      isOtherReason(
        cancellationReasons.find(
          reason => reason.id.toString() === selectedReasonId
        )
      )
  );

  const isSubmitDisabled =
    isSubmitting ||
    !selectedReasonId ||
    (isOtherSelected && !otherText) ||
    Boolean(otherTextError);

  if (isLoading) {
    return (
      <Box sx={cancelFormViewStyles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <FetchError />;
  }

  return (
    <>
      <Box sx={cancelFormViewStyles.container}>
        <Box sx={cancelFormViewStyles.contentContainer}>
          <Box sx={cancelFormViewStyles.imageContainer}>
            <Box
              component="img"
              src={CancelImageBackground}
              alt={TEXT.CANCEL_BACKGROUND_ALT}
              sx={cancelFormViewStyles.backgroundImage}
            />
            <Box
              component="img"
              src={CancelImage}
              alt={TEXT.CANCEL_ICON_ALT}
              sx={cancelFormViewStyles.cancelIcon}
            />
          </Box>
          <Typography variant="h5" sx={cancelFormViewStyles.titleText}>
            {TEXT.TITLE}
          </Typography>

          <Box sx={cancelFormViewStyles.radioGroupContainer}>
            <RadioGroup
              value={selectedReasonId}
              onChange={handleChange}
              sx={cancelFormViewStyles.radioGroup}
            >
              {cancellationReasons.map(reason => (
                <FormControlLabel
                  key={reason.id}
                  value={reason.id.toString()}
                  control={<Radio color="primary" />}
                  label={reason.name}
                />
              ))}
            </RadioGroup>
            {isOtherSelected && (
              <Box sx={cancelFormViewStyles.otherTextContainer}>
                <TextField
                  fullWidth
                  placeholder={TEXT.OTHER_PLACEHOLDER}
                  size="medium"
                  variant="outlined"
                  value={otherText}
                  data-testid="other-textarea"
                  onChange={handleOtherTextChange}
                  sx={cancelFormViewStyles.textField}
                />
                {otherTextError && (
                  <Typography variant="caption" color="error">
                    {otherTextError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box sx={cancelFormViewStyles.buttonsContainer}>
            <Stack
              sx={cancelFormViewStyles.buttonStack}
              direction="row"
              spacing={2}
            >
              <Button
                variant="outlined"
                fullWidth
                data-testid="cancel-button"
                onClick={handleCancel}
                sx={cancelFormViewStyles.cancelButton}
              >
                {TEXT.CANCEL_BUTTON}
              </Button>
              <Button
                variant="contained"
                fullWidth
                color="primary"
                data-testid="continue-button"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                sx={cancelFormViewStyles.submitButton}
              >
                {TEXT.REVIEW_BUTTON}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
      <Box sx={cancelFormViewStyles.footerSection}>
        <Footer />
      </Box>
    </>
  );
};

export default CancelFormView;
